// The resolver: NavContext -> NavSection[]. This is where (role x brand)
// composition, modifiers, and the per-item brand filter live. The matrix
// (nav-matrix.ts) supplies the ordered section list per cell; the resolver
// picks the cell, applies modifiers that the matrix can't express
// declaratively (billing visibility, profile-setup short-circuit), and then
// filters every leaf item through the SINGLE brand gate
// isRouteVisibleForBrand (route-brand.config.ts) — no parallel brand list
// (Critical Rule #8).

import { isRouteVisibleForBrand } from '~/config/route-brand.config';
import { MATRIX, type MatrixCellOptions } from './nav-matrix';
import {
  homeSection,
  inboxSection,
  membershipRequestPendingSection,
  profileSetupOnlySection,
} from './nav-sections';
import type { NavContext, NavRole, NavSection } from './nav-types';

/** Pick the dominant role for matrix lookup. Precedence:
 *  superAdmin > organizer > clientUser > dancer.
 *
 *  - A user who belongs to an organization lands in the `organizer` cell even
 *    if they are also a dancer — the organizer cell composes dancer surfaces
 *    from `m.isDancer` AND the org's competitions from `m.partnerCompetitions`,
 *    so the dancer-who-organizes keeps everything. Org *workspace* management
 *    lives in the separate team-account nav, reached via the account switcher.
 *  - An org member whose org participates in a competition (but who is not an
 *    `organizer` account) reaches those competitions from the `Competitions`
 *    group that every cell composes from `partnerCompetitions`; there is no
 *    separate `partner` cell.
 *  - `clientUser` stays gated on `!isDancer`: a dancer who is also a client
 *    uses the richer dancer cell, which the matrix composes with the client
 *    portal when `m.isClientUser` (the shipped BAL-340 composition).
 *  Composition lives in the matrix cell builders (they receive every flag);
 *  this only chooses the cell. */
function dominantRole(ctx: NavContext): NavRole {
  if (ctx.isSuperAdmin) return 'superAdmin';
  if (ctx.isOrganizer) return 'organizer';
  if (ctx.isClientUser && !ctx.isDancer) return 'clientUser';
  return 'dancer';
}

/** Drop a leaf whose path is owned by the other brand. Items with non-`/home`
 *  paths or unscoped paths pass through (isRouteVisibleForBrand returns true). */
function filterSectionForBrand(
  section: NavSection,
  brand: NavContext['brand'],
): NavSection | null {
  if (section.kind !== 'group') return section;

  const items = section.items.filter((item) =>
    isRouteVisibleForBrand(item.path, brand),
  );

  // A group that loses all its items is dropped entirely.
  if (items.length === 0) return null;

  return { ...section, items };
}

export function resolveSections(ctx: NavContext): NavSection[] {
  // Profile-setup short-circuit: a dancer who must finish setup sees only the
  // "Complete Profile" item.
  if (ctx.needsProfileSetup && ctx.isDancer) {
    return [profileSetupOnlySection];
  }

  // Membership-request-pending short-circuit (BAL-668): an org staff member
  // whose self-claim is awaiting org-admin approval holds no membership, no org
  // seat, and no dancer profile — the matrix would hand them a bare Home +
  // Inbox with no hint their request is in flight. Surface an informational
  // "request pending" notice between Home and Inbox. Only for the otherwise
  // role-less shell; a user who also holds a real role keeps that role's menu,
  // so `hasPendingOrgJoinRequest` (= onboarded org staff) reads as "pending"
  // exactly here. All three sections point at /home*, so no brand filter is
  // needed (mirrors the profile-setup early return above).
  const isBareShell =
    !ctx.isDancer &&
    !ctx.isOrganizer &&
    !ctx.isClientUser &&
    !ctx.isSuperAdmin &&
    ctx.partnerCompetitions.length === 0;
  if (ctx.hasPendingOrgJoinRequest && isBareShell) {
    return [homeSection, membershipRequestPendingSection, inboxSection];
  }

  const role = dominantRole(ctx);

  const options: MatrixCellOptions = {
    isSuperAdmin: ctx.isSuperAdmin,
    isClientUser: ctx.isClientUser,
    isDancer: ctx.isDancer,
    isOrganizer: ctx.isOrganizer,
    isSeller: ctx.isSeller,
    hasPerformanceData: ctx.hasPerformanceData,
    hasReimbursements: ctx.hasReimbursements,
    hasDancerInvoicing: ctx.hasDancerInvoicing,
    clientRole: ctx.clientRole,
    // Client users see billing only if they are owners; super admins and
    // non-client users always can.
    canSeeBilling:
      !ctx.isClientUser || ctx.clientRole === 'owner' || ctx.isSuperAdmin,
    partnerCompetitions: ctx.partnerCompetitions,
    dancerProfileSlug: ctx.dancerProfileSlug,
  };

  const cell = MATRIX[ctx.brand][role](options);

  return cell
    .map((section) => filterSectionForBrand(section, ctx.brand))
    .filter((section): section is NavSection => section !== null);
}
